import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { MaintenanceImage } from '../../types';
import { useFocusTrap } from '../../hooks/useFocusTrap';

interface RichTextEditorProps {
  value: string;
  onChange: (value: string) => void;
  availableImages: MaintenanceImage[];
  placeholder?: string;
  disabled?: boolean;
}

const COLORS = [
    '#000000', '#4b5563', '#ef4444', '#f97316', '#eab308', '#22c55e', '#3b82f6', '#8b5cf6',
    '#ffffff', '#d1d5db', '#fca5a5', '#fdba74', '#fde047', '#86efac', '#93c5fd', '#c4b5fd',
];

const RichTextEditor: React.FC<RichTextEditorProps> = ({ value, onChange, availableImages, placeholder, disabled }) => {
  const editorRef = useRef<HTMLDivElement>(null);
  const [activeStyles, setActiveStyles] = useState<{ [key: string]: any }>({});
  
  // State for popover visibility (for colors)
  const [popover, setPopover] = useState<string | null>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  // State for image embed modal
  const [isImageModalOpen, setIsImageModalOpen] = useState(false);
  
  const selectionRef = useRef<Range | null>(null);

  // Memoize and sort the available images to ensure a stable order in the modal.
  const sortedImages = useMemo(() => 
    [...availableImages].sort((a, b) => a.id - b.id), 
  [availableImages]);

  // Sync editor content with the value prop from parent
  useEffect(() => {
    if (editorRef.current && editorRef.current.innerHTML !== value) {
      editorRef.current.innerHTML = value || '';
    }
  }, [value]);

  // Handle closing popovers when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (popoverRef.current && !popoverRef.current.contains(event.target as Node)) {
        setPopover(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const saveSelection = () => {
    const selection = window.getSelection();
    if (selection && selection.rangeCount > 0 && editorRef.current?.contains(selection.anchorNode)) {
        selectionRef.current = selection.getRangeAt(0);
    }
  };

  const restoreSelection = () => {
    if (selectionRef.current) {
        const selection = window.getSelection();
        if (selection) {
            selection.removeAllRanges();
            selection.addRange(selectionRef.current);
        }
    }
  };

  const handlePopoverOpen = (popoverName: string) => {
      saveSelection();
      setPopover(popover === popoverName ? null : popoverName);
  };
  
  const handleOpenImageModal = () => {
    saveSelection();
    setIsImageModalOpen(true);
  };

  // Callback to update which formatting buttons should appear as "active"
  const updateActiveStyles = useCallback(() => {
    const newStyles: { [key: string]: any } = {};
    const commands: string[] = ['bold', 'italic', 'underline', 'strikethrough', 'superscript', 'subscript', 'insertUnorderedList', 'insertOrderedList'];
    commands.forEach(cmd => {
      if (document.queryCommandState(cmd)) newStyles[cmd] = true;
    });

    let node = window.getSelection()?.anchorNode;
    while (node && node !== editorRef.current) {
      if (node.nodeName === 'UL' && (node as HTMLElement).classList.contains('checklist')) {
        newStyles['checklist'] = true;
      }
      if(node.nodeName.match(/^(P|DIV|H[1-6])$/)) {
        const align = (node as HTMLElement).style.textAlign;
        if(['left', 'center', 'right', 'justify'].includes(align)) {
            newStyles['align'] = align;
        } else {
            // Default alignment is left
            newStyles['align'] = 'left';
        }
        break;
      }
      node = node.parentNode;
    }
    
    setActiveStyles(newStyles);
  }, []);
  
  useEffect(() => {
    const editor = editorRef.current;
    if (!editor) return;
    const handleSelectionChange = () => {
        if(document.activeElement === editor) updateActiveStyles();
    };
    document.addEventListener('selectionchange', handleSelectionChange);
    ['input', 'click', 'keyup', 'focus'].forEach(ev => editor.addEventListener(ev, updateActiveStyles));
    updateActiveStyles(); // Initial check
    return () => {
      document.removeEventListener('selectionchange', handleSelectionChange);
      ['input', 'click', 'keyup', 'focus'].forEach(ev => editor.removeEventListener(ev, updateActiveStyles));
    };
  }, [updateActiveStyles]);


  const handleInput = (e: React.FormEvent<HTMLDivElement>) => {
    onChange(e.currentTarget.innerHTML);
  };

  const applyCommand = (command: string, value: string | null = null) => {
    document.execCommand(command, false, value);
    editorRef.current?.focus();
    if(editorRef.current) onChange(editorRef.current.innerHTML);
    updateActiveStyles();
  };
  
  const applyCommandFromPopover = (command: string, value: string | null = null) => {
    editorRef.current?.focus();
    restoreSelection();
    document.execCommand(command, false, value);
    if(editorRef.current) onChange(editorRef.current.innerHTML);
    setPopover(null);
    updateActiveStyles();
  };
  
  const handleImageSelect = (image: MaintenanceImage) => {
    // For immediate display, use the current valid URL.
    // For long-term persistence, embed a stable identifier via data-attribute.
    const stableIdAttribute = `data-image-id="${image.id}"`;
    const imgTag = `<p style="text-align: center;"><img src="${image.image_url_s3}" ${stableIdAttribute} alt="Embedded attachment" style="max-width: 90%; height: auto; border-radius: 0.5rem; display: inline-block;"></p><p><br></p>`;
    
    editorRef.current?.focus();
    restoreSelection();
    document.execCommand('insertHTML', false, imgTag);
    
    if (editorRef.current) {
        onChange(editorRef.current.innerHTML);
    }
    
    updateActiveStyles();
    setIsImageModalOpen(false); // Close the modal
  };

  const toggleChecklist = () => {
    applyCommand('insertUnorderedList');
    setTimeout(() => {
        const selection = window.getSelection();
        if (!selection || !selection.rangeCount) return;
        let node = selection.getRangeAt(0).startContainer;
        while (node && node !== editorRef.current) {
            if (node.nodeName === 'UL') {
                (node as HTMLElement).classList.toggle('checklist');
                if(editorRef.current) onChange(editorRef.current.innerHTML);
                break;
            }
            node = node.parentNode;
        }
        updateActiveStyles();
    }, 0);
  };
  
  const containerClasses = `
    border rounded-lg overflow-hidden transition-all duration-200
    ${disabled ? 'bg-gray-100' : 'bg-white'}
    border-gray-300 focus-within:border-indigo-500 focus-within:ring-1 focus-within:ring-indigo-500
  `;

  return (
    <div className={containerClasses}>
      <div className="flex flex-nowrap items-center p-1.5 border-b border-gray-200 bg-gray-50/70 rounded-t-md gap-x-0.5 overflow-x-auto">
        {/* Text style controls */}
        <div className="flex items-center">
            <ToolbarButton onClick={() => applyCommand('bold')} label="Bold" isActive={activeStyles.bold} disabled={disabled}>
                <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10.5V5.5C13 4.67 12.33 4 11.5 4H8.5C7.12 4 6 5.12 6 6.5S7.12 9 8.5 9H11.5C12.33 9 13 9.67 13 10.5S12.33 12 11.5 12H9.5C8.12 12 7 13.12 7 14.5S8.12 17 9.5 17H12.5C13.88 17 15 15.88 15 14.5S13.88 12 12.5 12H11" /></svg>
            </ToolbarButton>
            <ToolbarButton onClick={() => applyCommand('italic')} label="Italic" isActive={activeStyles.italic} disabled={disabled}>
                <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 4h6M7 20h6M12.5 4L8.5 20" /></svg>
            </ToolbarButton>
            <ToolbarButton onClick={() => applyCommand('underline')} label="Underline" isActive={activeStyles.underline} disabled={disabled}>
                <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 4v7a4 4 0 0 0 8 0V4M4 21h16" /></svg>
            </ToolbarButton>
            <ToolbarButton onClick={() => applyCommand('strikethrough')} label="Strikethrough" isActive={activeStyles.strikethrough} disabled={disabled}>
                <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 12h14" /><path d="M16 6.5a4 4 0 0 0-8 0V10a4 4 0 0 0 8 0V6.5z" /></svg>
            </ToolbarButton>
        </div>
       
        <ToolbarSeparator />
        
        <div className="flex items-center">
            {/* Color controls */}
            <div className="relative">
                <ToolbarButton onClick={() => handlePopoverOpen('text-color')} label="Text Color" disabled={disabled}>
                    <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 13l3 3 3-3m-6 0h6" /><path d="M4 19h16" /><path d="M10.83 5l-2.33 6.5h4.66L10.83 5z" /></svg>
                </ToolbarButton>
                <Popover isOpen={popover === 'text-color'} ref={popoverRef}>
                    <ColorGrid colors={COLORS} onSelect={(color) => applyCommandFromPopover('foreColor', color)} />
                </Popover>
            </div>
            <div className="relative">
                <ToolbarButton onClick={() => handlePopoverOpen('bg-color')} label="Highlight Color" disabled={disabled}>
                    <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M14 2.5L16.5 5 14 7.5 11.5 5 14 2.5Z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11.5 5L20 13.5 17.5 16 9 7.5 11.5 5Z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 13.5L9 18.5 2 22 4 13.5Z" /></svg>
                </ToolbarButton>
                <Popover isOpen={popover === 'bg-color'} ref={popoverRef}>
                    <ColorGrid colors={COLORS} onSelect={(color) => applyCommandFromPopover('hiliteColor', color)} />
                </Popover>
            </div>
        </div>

        <ToolbarSeparator />

        <div className="flex items-center">
            {/* Script controls */}
            <ToolbarButton onClick={() => applyCommand('superscript')} label="Superscript" isActive={activeStyles.superscript} disabled={disabled}>
                <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 7L9 3 13 7" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 3v13" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M14 5h3a1 1 0 0 1 1 1v1a1 1 0 0 1-1 1h-2a1 1 0 0 0-1 1v1a1 1 0 0 0 1 1h3" /></svg>
            </ToolbarButton>
            <ToolbarButton onClick={() => applyCommand('subscript')} label="Subscript" isActive={activeStyles.subscript} disabled={disabled}>
                <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 17l4-4 4 4" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 21V7" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M14 15h3a1 1 0 0 1 1 1v1a1 1 0 0 1-1 1h-2a1 1 0 0 0-1 1v1a1 1 0 0 0 1 1h3" /></svg>
            </ToolbarButton>
        </div>

        <ToolbarSeparator />

        <div className="flex items-center">
            {/* Alignment controls */}
            <ToolbarButton onClick={() => applyCommand('justifyLeft')} label="Align Left" isActive={activeStyles.align === 'left'} disabled={disabled}>
              <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M2 4.75A.75.75 0 012.75 4h14.5a.75.75 0 010 1.5H2.75A.75.75 0 012 4.75zM2 9.75A.75.75 0 012.75 9h14.5a.75.75 0 010 1.5H2.75A.75.75 0 012 9.75zM2.75 14a.75.75 0 000 1.5h14.5a.75.75 0 000-1.5H2.75z" clipRule="evenodd" /></svg>
            </ToolbarButton>
            <ToolbarButton onClick={() => applyCommand('justifyCenter')} label="Align Center" isActive={activeStyles.align === 'center'} disabled={disabled}>
              <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M2 4.75A.75.75 0 012.75 4h14.5a.75.75 0 010 1.5H2.75A.75.75 0 012 4.75zM5.25 9a.75.75 0 01.75.75h8a.75.75 0 010-1.5h-8A.75.75 0 015.25 9zM2 14.75A.75.75 0 012.75 14h14.5a.75.75 0 010 1.5H2.75A.75.75 0 012 14.75z" clipRule="evenodd" /></svg>
            </ToolbarButton>
            <ToolbarButton onClick={() => applyCommand('justifyRight')} label="Align Right" isActive={activeStyles.align === 'right'} disabled={disabled}>
              <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M2 4.75A.75.75 0 012.75 4h14.5a.75.75 0 010 1.5H2.75A.75.75 0 012 4.75zM2 9.75A.75.75 0 012.75 9h14.5a.75.75 0 010 1.5H2.75A.75.75 0 012 9.75zM17.25 14a.75.75 0 000 1.5H2.75a.75.75 0 000-1.5h14.5z" clipRule="evenodd" /></svg>
            </ToolbarButton>
            <ToolbarButton onClick={() => applyCommand('justifyFull')} label="Justify" isActive={activeStyles.align === 'justify'} disabled={disabled}>
              <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M2 4.75A.75.75 0 012.75 4h14.5a.75.75 0 010 1.5H2.75A.75.75 0 012 4.75zM2 9.75A.75.75 0 012.75 9h14.5a.75.75 0 010 1.5H2.75A.75.75 0 012 9.75zM2 14.75A.75.75 0 012.75 14h14.5a.75.75 0 010 1.5H2.75A.75.75 0 012 14.75z" clipRule="evenodd" /></svg>
            </ToolbarButton>
        </div>
        
        <ToolbarSeparator />

        <div className="flex items-center">
            {/* List controls */}
            <ToolbarButton onClick={() => applyCommand('insertUnorderedList')} label="Bulleted List" isActive={activeStyles.insertUnorderedList && !activeStyles.checklist} disabled={disabled}>
              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h7" /><circle cx="4" cy="6" r="1" stroke="none" fill="currentColor"/><circle cx="4" cy="12" r="1" stroke="none" fill="currentColor"/><circle cx="4" cy="18" r="1" stroke="none" fill="currentColor"/></svg>
            </ToolbarButton>
            <ToolbarButton onClick={() => applyCommand('insertOrderedList')} label="Numbered List" isActive={activeStyles.insertOrderedList} disabled={disabled}>
              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 6h11M9 12h11M9 18h11M5 6h.01M5 12h.01M5 18h.01" /></svg>
            </ToolbarButton>
            <ToolbarButton onClick={toggleChecklist} label="Checklist" isActive={activeStyles.checklist} disabled={disabled}>
                <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" /></svg>
            </ToolbarButton>
        </div>

        <ToolbarSeparator />

        <div className="flex items-center">
            {/* Indent controls */}
            <ToolbarButton onClick={() => applyCommand('indent')} label="Indent" disabled={disabled}>
                <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 5h8M13 9h8M13 13h8M13 17h8M3 8l4 4-4 4" /></svg>
            </ToolbarButton>
            <ToolbarButton onClick={() => applyCommand('outdent')} label="Outdent" disabled={disabled}>
                <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5h8M11 9h8M11 13h8M11 17h8M7 8L3 12l4 4" /></svg>
            </ToolbarButton>
        </div>
        
        <ToolbarSeparator />

        <div className="flex items-center">
            {/* Embed controls */}
            <ToolbarButton onClick={handleOpenImageModal} label="Embed Image" disabled={disabled || availableImages.length === 0}>
                <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>
            </ToolbarButton>

            <ToolbarSeparator />
            
            {/* Clear formatting */}
            <ToolbarButton onClick={() => applyCommand('removeFormat')} label="Clear Formatting" disabled={disabled}>
                <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v1m6 11h2.5a2.5 2.5 0 0 0 0-5h-13a2.5 2.5 0 0 0 0 5H9" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4L8 8" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4l4 4" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 20h18" /></svg>
            </ToolbarButton>
        </div>
      </div>

      <div
        ref={editorRef}
        className="w-full p-3 min-h-[120px] rte-editor text-gray-900"
        contentEditable={!disabled}
        onInput={handleInput}
        data-placeholder={placeholder}
      />

      {isImageModalOpen && (
        <ImageEmbedModal
          images={sortedImages}
          onClose={() => setIsImageModalOpen(false)}
          onSelect={handleImageSelect}
        />
      )}
    </div>
  );
};

const ToolbarButton: React.FC<{ onClick: () => void; label: string; isActive?: boolean; disabled?: boolean; children: React.ReactNode }> = ({ onClick, label, isActive, disabled, children }) => {
    const buttonClasses = `p-2 rounded-md transition-colors ${isActive ? 'bg-indigo-200 text-indigo-700' : 'text-gray-600 hover:bg-gray-200'} focus:outline-none focus:ring-1 focus:ring-offset-1 focus:ring-indigo-400 focus:ring-offset-gray-50/70 disabled:text-gray-400 disabled:hover:bg-transparent disabled:cursor-not-allowed`;
    return <button type="button" onClick={onClick} className={buttonClasses} aria-label={label} title={label} disabled={disabled} aria-pressed={isActive}>{children}</button>;
};

const ToolbarSeparator: React.FC = () => <div className="w-px h-6 bg-gray-300 mx-1.5"></div>;

const Popover = React.forwardRef<HTMLDivElement, { isOpen: boolean; children: React.ReactNode }>(({ isOpen, children }, ref) => {
    if (!isOpen) return null;
    return (
        <div ref={ref} className="absolute top-full left-0 mt-2 min-w-[150px] bg-white border border-gray-200 rounded-lg shadow-lg z-10 p-2 animate-fade-in-down origin-top-left">
            {children}
        </div>
    );
});

const ColorGrid: React.FC<{ colors: string[], onSelect: (color: string) => void }> = ({ colors, onSelect }) => (
    <div className="grid grid-cols-8 gap-1 p-1">
        {colors.map(color => (
            <button key={color} onClick={() => onSelect(color)} className="w-6 h-6 rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-indigo-500" style={{ backgroundColor: color }} aria-label={color}></button>
        ))}
    </div>
);

interface ImageEmbedModalProps {
  images: MaintenanceImage[];
  onClose: () => void;
  onSelect: (image: MaintenanceImage) => void;
}

const ImageEmbedModal: React.FC<ImageEmbedModalProps> = ({ images, onClose, onSelect }) => {
  const [isShowing, setIsShowing] = useState(false);
  const modalRef = useRef<HTMLDivElement>(null);
  
  useFocusTrap(modalRef, isShowing);

  useEffect(() => {
    setIsShowing(true);
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        handleClose();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  const handleClose = () => {
    setIsShowing(false);
    setTimeout(onClose, 300);
  };
  
  return (
    <div
      className={`fixed inset-0 bg-black z-50 flex justify-center items-center transition-opacity duration-300 ${isShowing ? 'bg-opacity-60' : 'bg-opacity-0'}`}
      aria-labelledby="embed-image-modal-title"
      role="dialog"
      aria-modal="true"
      onClick={handleClose}
    >
      <div
        ref={modalRef}
        className={`bg-white rounded-xl shadow-2xl w-full max-w-2xl m-4 transform transition-all duration-300 ease-out flex flex-col max-h-[calc(100vh-80px)] ${isShowing ? 'scale-100 opacity-100' : 'scale-95 opacity-0'}`}
        role="document"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="p-5 border-b border-gray-200">
          <div className="flex justify-between items-center">
            <h2 id="embed-image-modal-title" className="text-xl font-bold text-gray-900">Select an Image to Embed</h2>
            <button 
              type="button" 
              onClick={handleClose}
              className="text-gray-400 hover:text-gray-600 rounded-full p-1 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              aria-label="Close"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </header>
        <main className="p-6 overflow-y-auto">
          {images.length > 0 ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
              {images.map(image => (
                <button
                  key={image.id}
                  onClick={() => onSelect(image)}
                  className="aspect-square w-full h-full rounded-lg overflow-hidden group focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-all"
                  aria-label={`Embed image ${image.id}`}
                >
                  <img src={image.image_url_s3} alt={`Attachment ${image.id}`} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300" />
                   <div className="absolute inset-0 bg-black/10 group-hover:bg-black/30 transition-colors flex items-center justify-center">
                     <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-white opacity-0 group-hover:opacity-100 transition-opacity" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm.707-10.707a1 1 0 00-1.414 0L6 10.586V7a1 1 0 10-2 0v4a1 1 0 001 1h4a1 1 0 100-2H7.414l3.293-3.293a1 1 0 000-1.414z" clipRule="evenodd" />
                    </svg>
                   </div>
                </button>
              ))}
            </div>
          ) : (
            <p className="text-center text-gray-500 py-10">No attachments available to embed.</p>
          )}
        </main>
      </div>
    </div>
  );
};

export default RichTextEditor;